<template>
  <div
    :class="[
      'MapLegend flex! flex-row! flex-nowrap! relative',
      inline ? 'MapLegend--inline' : 'maplibregl-ctrl mr-[0.3rem]! maplibregl-ctrl-group',
    ]"
    :isOpen="legendIsShown"
  >
    <MapControlTooltip :text="$t('geoportal.mapControls.legend')">
      <button
        :aria-label="$t('geoportal.mapControls.legend')"
        @click.stop="legendIsShown = !legendIsShown"
        :class="{ isActive: legendIsShown }"
        class="rounded-full border-none cursor-pointer inline-flex items-center justify-center transition-opacity duration-200"
      >
        <Icon icon="$layers" color="var(--color-main)" />
      </button>
    </MapControlTooltip>

    <div
      v-if="legendIsShown"
      class="MapLegend__panel right-12 top-0 absolute flex flex-col w-60 bg-white rounded-lg shadow-lg"
      @dragover.stop
      @drop.stop
      @dragleave.stop
    >
      <div class="flex justify-between items-center border-b px-2 py-1.5 border-gray-100 shrink-0">
        <h3 class="text-base font-semibold text-gray-900">
          {{ $t('myMap.legend.title') }}
        </h3>

        <button
          @click.stop="legendIsShown = false"
          class="size-8! text-gray-400 hover:text-gray-600 shadow-none! transition-colors p-1 rounded-full hover bg-white! hover:bg-gray-100! cursor-pointer center-blocs"
        >
          <Icon icon="mdi:close" size="20" />
        </button>
      </div>

      <div class="MapLegend__scrollArea px-2 py-2">
        <VueDraggable v-model="mapStore.legendList" @end="updateMainLayerOrder" group="parent">
          <div
            class="flex flex-col"
            :class="{
              'border border-[rgb(var(--v-theme-main-grey))] rounded-[5px]':
                item.layerType === layerType.ATLAS_LAYER,
            }"
            v-for="item in mapStore.legendList"
            :key="item.id"
            :item-type="item.layerType"
          >
            <div
              class="flex flex-row items-center justify-between w-full cursor-grab hover:bg-gray-100 transition-colors pl-1 rounded"
            >
              <div class="flex items-center min-w-0 flex-1">
                <Icon icon="$drag" color="dark-grey" class="shrink-0" />
                <img
                  loading="lazy"
                  :src="item.icon"
                  v-if="item.layerType === layerType.APP_LAYER"
                  class="w-4 h-4 object-cover ml-[0.1rem] shrink-0"
                />
                <Icon icon="$layersOutline" color="black" v-else class="shrink-0" />
                <span class="text-sm font-medium capitalize ml-1 truncate">{{ item.name }}</span>
              </div>
              <div class="flex items-center shrink-0">
                <button
                  v-if="!('atlasGroup' in item && item.atlasGroup === atlasGroup.PREDEFINED_MAP)"
                  @click="removeMainLayer(item)"
                  class="size-8! bg-transparent! shadow-none! border-none cursor-pointer p-1 inline-flex items-center justify-center transition-opacity duration-200 hover:bg-transparent!"
                  :aria-label="$t('myMap.legend.removeLayer')"
                >
                  <Icon icon="material-symbols-light:delete" color="dark-grey" />
                </button>
              </div>
            </div>

            <template v-if="item.layerType === layerType.ATLAS_LAYER">
              <VueDraggable
                v-model="item.subLayers"
                @end="updateSubLayerOrder(item)"
                :group="item.id"
              >
                <div
                  class="flex flex-row items-center justify-between w-full cursor-grab pl-3"
                  v-for="subItem in item.subLayers"
                  :key="subItem.name"
                >
                  <div class="flex items-center">
                    <Icon icon="$drag" color="dark-grey" />
                    <img
                      loading="lazy"
                      :src="subItem.icon"
                      class="w-4 h-4 object-cover ml-[0.1rem]"
                    />
                    <span class="text-xs capitalize ml-1">{{ subItem.name }}</span>
                  </div>
                  <div class="flex items-center">
                    <button
                      v-if="'atlasGroup' in item && item.atlasGroup === atlasGroup.THEMATIC_DATA"
                      @click="removeSubLayer(item, subItem.name)"
                      class="bg-transparent border-none cursor-pointer p-1 inline-flex items-center justify-center transition-opacity duration-200 hover:bg-[var(--color-main-hover)] mr-1"
                    >
                      <Icon icon="$deleteOutline" color="dark-grey" />
                    </button>
                  </div>
                </div>
              </VueDraggable>
            </template>
          </div>
        </VueDraggable>

        <template v-if="mapStore.unifiedMapLegendList.length > 0">
          <div
            :class="{ 'border-t border-gray-100 pt-2 mt-2': mapStore.legendList.length > 0 }"
          >
            <VueDraggable
              v-model="mapStore.unifiedMapLegendList"
              @end="onUnifiedLegendReorder"
              group="unifiedMapLegend"
            >
              <div
                v-for="entry in mapStore.unifiedMapLegendList"
                :key="`${entry.type}_${entry.id}`"
                class="flex flex-row items-center justify-between w-full cursor-grab hover:bg-gray-100 transition-colors pl-1 rounded py-0.5"
              >
                <div class="flex items-center min-w-0 flex-1">
                  <Icon icon="$drag" color="dark-grey" class="shrink-0" />
                  <span
                    class="text-sm font-medium ml-1 truncate"
                    :class="{ 'opacity-40': entry.hidden }"
                  >{{ entry.name }}</span>
                </div>
                <div class="flex items-center shrink-0">
                  <button
                    v-if="entry.type === 'userDropped'"
                    type="button"
                    class="size-7! bg-transparent! shadow-none! border-none cursor-pointer p-0.5 inline-flex items-center justify-center hover:bg-gray-200 rounded"
                    :aria-label="$t('myMap.legend.zoomToLayer', 'Zoomer sur la couche')"
                    :title="$t('myMap.legend.zoomToLayer', 'Zoomer sur la couche')"
                    @click="mapStore.zoomToUserDroppedLayer(entry.id)"
                  >
                    <Icon icon="mdi:fit-to-screen-outline" color="dark-grey" size="18" />
                  </button>
                  <button
                    v-if="entry.type === 'osm' && mapStore.osmClusteredStyleIds.includes(entry.id)"
                    type="button"
                    class="size-7! bg-transparent! shadow-none! border-none cursor-pointer p-0.5 inline-flex items-center justify-center hover:bg-gray-200 rounded"
                    :aria-label="$t('myMap.rightSidebar.actions.heatmap')"
                    :title="$t('myMap.rightSidebar.actions.heatmap')"
                    @click.stop="mapStore.toggleOsmHeatmap(entry.id)"
                  >
                    <Icon
                      icon="mdi:chart-scatter-plot"
                      :color="mapStore.osmHeatmapStyleIds.has(entry.id) ? 'main-blue' : 'dark-grey'"
                      size="18"
                    />
                  </button>
                  <button
                    type="button"
                    class="size-7! bg-transparent! shadow-none! border-none cursor-pointer p-0.5 inline-flex items-center justify-center hover:bg-gray-200 rounded"
                    :aria-label="entry.hidden ? $t('myMap.legend.showLayer', 'Afficher') : $t('myMap.legend.hideLayer', 'Masquer')"
                    @click="mapStore.toggleUnifiedMapLegendEntryVisibility(entry)"
                  >
                    <Icon
                      :icon="entry.hidden ? 'mdi:eye-off-outline' : 'mdi:eye-outline'"
                      :color="entry.hidden ? 'grey' : 'dark-grey'"
                      size="18"
                    />
                  </button>
                  <button
                    type="button"
                    class="size-7! bg-transparent! shadow-none! border-none cursor-pointer p-0.5 inline-flex items-center justify-center hover:bg-gray-200 rounded"
                    :aria-label="$t('myMap.legend.removeLayer')"
                    @click="mapStore.removeUnifiedMapLegendEntry(entry)"
                  >
                    <Icon icon="material-symbols-light:delete" color="dark-grey" size="18" />
                  </button>
                </div>
              </div>
            </VueDraggable>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  inline?: boolean
}>()

import Icon from '@/components/ui/Icon.vue'
import MapControlTooltip from '@/components/map/controls/MapControlTooltip.vue'
import { ItemType } from '@/models/enums/app/ItemType'
import { AtlasGroup } from '@/models/enums/geo/AtlasGroup'
import { LayerType } from '@/models/enums/geo/LayerType'
import type { AppLayerLegendItem, AtlasLayerLegendItem } from '@/models/interfaces/map/Legend'
import { useMyMapStore } from '@/stores/myMapStore'
import { ref, watch } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
const layerType = LayerType
const atlasGroup = AtlasGroup

const legendIsShown = ref(false)
const mapStore = useMyMapStore()

watch(legendIsShown, (isShown) => {
  if (isShown) mapStore.syncUnifiedMapLegend()
})

function updateMainLayerOrder() {
  mapStore.legendList.forEach((item, index) => {
    item.order = index
  })
  mapStore.updateMapLayersOrder()
}

function updateSubLayerOrder(item: AtlasLayerLegendItem) {
  item.subLayers.forEach((subItem, index) => {
    subItem.order = index
  })
  mapStore.updateAtlasSubLayersOrder(item)
}

function removeMainLayer(item: AtlasLayerLegendItem | AppLayerLegendItem) {
  if (item.layerType === LayerType.ATLAS_LAYER) {
    mapStore.atlasMaps.map((x) => {
      if (x.id === item.id) {
        x.mainLayer.isShown = false
        x.subLayers.map((x) => (x.isShown = false))
      }
    })
  } else {
    switch (item.id) {
      case ItemType.ACTOR:
        mapStore.actorLayer!.isShown = false
        break
      case ItemType.PROJECT:
        mapStore.projectLayer!.isShown = false
        break
      case ItemType.RESOURCE:
        mapStore.resourceLayer!.isShown = false
        break
    }
  }
}

function removeSubLayer(item: AtlasLayerLegendItem, subLayerName: string) {
  mapStore.atlasMaps.map((x) => {
    if (x.id === item.id) {
      x.subLayers.map((x) => {
        if (x.name === subLayerName) {
          x.isShown = false
        }
      })
    }
  })
  mapStore.updateAtlasLayersVisibility(item.id)
}

function onUnifiedLegendReorder() {
  mapStore.reorderUnifiedMapLayers()
}
</script>

<style>
.MapLegend__panel {
  max-height: calc(100vh - var(--mymap-header-h, 60px) - 3rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.MapLegend__scrollArea {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}
</style>
